import { ReactNode, useState } from 'react'
import { TFunction } from 'i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import {
  ArrowLeft,
  Check,
  CircleSlash,
  ExternalLink,
  Plus,
  Shapes,
  Trash2,
} from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import Card from 'app/frontend/base/components/Card'
import { cn } from '@/lib/utils'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import { RaidPlanEncounterAssignmentAbility } from 'app/frontend/types/custom/RaidPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import { createVisualPlan } from 'app/frontend/base/api/visual_plans'
import {
  useEncounterVisualPlans,
  useVisualPlan,
} from 'app/frontend/base/config/queries'
import { extractUuid, getRaidPlanLinkInfo } from './raidPlanLink'
import { countScreenEntitySlots } from './visual_plans/shapes/entityAssignmentMatching'
import VisualCanvas from './visual_plans/VisualCanvas'
import VisualPlanThumbnail from './VisualPlanThumbnail'
import useVisualPlanLiveSync from './visual_plans/hooks/useVisualPlanLiveSync'
import { useVisualPlansEnabledForEncounter } from '../visualPlansAvailability'

type View = 'chooser' | 'picker' | 'raidstrats'

type Props = {
  ability: RaidPlanEncounterAssignmentAbility
  encounterId: number | undefined
  difficulty: Difficulty
  owner: RaidPlanOwner
  basePath: string
  canEdit: boolean
  onUpdate: (
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
  t: TFunction
}

const SubviewHeader = ({
  title,
  onBack,
  actions,
}: {
  title: string
  onBack?: () => void
  actions?: ReactNode
}) => (
  <div className='flex items-center justify-between gap-2'>
    <div className='flex items-center gap-1 min-w-0'>
      {onBack ? (
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-8 w-8 shrink-0'
          onClick={onBack}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
      ) : null}
      <div className='text-sm font-medium truncate'>{title}</div>
    </div>
    {actions}
  </div>
)

const AbilityVisualPopover = ({
  ability,
  encounterId,
  difficulty,
  owner,
  basePath,
  canEdit,
  onUpdate,
  onCommit,
  t,
}: Props) => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<View>('chooser')
  const visualPlansEnabled = useVisualPlansEnabledForEncounter(encounterId)

  const linkInfo = getRaidPlanLinkInfo(ability.raid_plan_link)
  const hasLink = (ability.raid_plan_link ?? '').trim().length > 0
  const hasVisual = ability.visual_id !== undefined
  const isActive = linkInfo.hasValidRaidPlanId || hasVisual

  const { data: linkedVisual } = useVisualPlan(owner, ability.visual_id)
  useVisualPlanLiveSync({ owner, visualId: ability.visual_id })
  const { data: encounterVisuals } = useEncounterVisualPlans(
    owner,
    encounterId,
    difficulty,
    isOpen && visualPlansEnabled,
  )

  const visualUrl = (visualId: number) =>
    owner.ownerType === 'team'
      ? `${basePath}/visuals/${visualId}`
      : `/profile/plans/visuals/${visualId}`

  const createMutation = useMutation({
    mutationFn: async () => {
      if (encounterId === undefined) throw new Error('Encounter missing')
      return await createVisualPlan(owner, {
        encounter_id: encounterId,
        difficulty,
        name: t('dashboard.team.plans.visuals.default-name'),
        screens: [{ id: uuid(), image_index: 0, shapes: [] }],
      })
    },
    onSuccess: async (visual) => {
      await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      onUpdate((current) => {
        current.visual_id = visual.id
        current.raid_plan_link = undefined
      }, true)
      setView('chooser')
      window.open(visualUrl(visual.id), '_blank', 'noopener')
    },
  })

  const triggerButton = (
    <Button
      type='button'
      variant='outline'
      className={cn(
        'min-w-20 justify-between gap-2',
        isActive ? 'border-primary' : '',
      )}
    >
      <span>{t('dashboard.team.plans.assignments.raid-plan-link')}</span>
      <span className='inline-flex h-4 w-4 items-center justify-center'>
        {isActive ? (
          <Check className='h-4 w-4 text-primary' />
        ) : (
          <CircleSlash className='h-4 w-4 text-muted-foreground' />
        )}
      </span>
    </Button>
  )

  const allScreenIds = linkedVisual?.screens.map((screen) => screen.id) ?? []
  const activeScreenIdSet = new Set(ability.visual_screen_ids ?? allScreenIds)
  const toggleScreen = (screenId: string) => {
    const nextActive = new Set(activeScreenIdSet)
    if (nextActive.has(screenId)) nextActive.delete(screenId)
    else nextActive.add(screenId)
    const nextIds = allScreenIds.filter((id) => nextActive.has(id))
    onUpdate((current) => {
      current.visual_screen_ids =
        nextIds.length === allScreenIds.length ? undefined : nextIds
    }, true)
  }

  const options = [
    {
      key: 'visual',
      icon: Shapes,
      title: t('dashboard.team.plans.assignments.visual-create-title'),
      onSelect: () => setView('picker'),
    },
    {
      key: 'raidstrats',
      icon: ExternalLink,
      title: t('dashboard.team.plans.assignments.visual-raidstrats-title'),
      onSelect: () => setView('raidstrats'),
    },
  ]

  const showChooser = visualPlansEnabled && !hasVisual && view === 'chooser'
  const showPicker = visualPlansEnabled && !hasVisual && view === 'picker'
  const showRaidstrats =
    !hasVisual && (view === 'raidstrats' || !visualPlansEnabled)

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (open) {
          if (!visualPlansEnabled) {
            setView('raidstrats')
          } else {
            setView(!hasVisual && hasLink ? 'raidstrats' : 'chooser')
          }
        } else {
          setView('chooser')
        }
      }}
    >
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        className={cn('space-y-2', showPicker ? 'w-[32rem]' : 'w-96')}
      >
        {hasVisual ? (
          <div className='space-y-2'>
            <div className='text-sm font-medium truncate'>
              {linkedVisual?.name ??
                t('dashboard.team.plans.visuals.default-name')}
            </div>
            {linkedVisual && linkedVisual.screens.length > 0 ? (
              <div className='space-y-1.5'>
                <p className='text-xs text-muted-foreground'>
                  {t(
                    'dashboard.team.plans.assignments.visual-screens-explanation',
                  )}
                </p>
                <div className='max-h-72 space-y-2 overflow-y-auto pr-1'>
                  {linkedVisual.screens.map((screen, index) => {
                    const isScreenActive = activeScreenIdSet.has(screen.id)
                    return (
                      <Card
                        key={screen.id}
                        role='button'
                        tabIndex={canEdit ? 0 : -1}
                        onClick={() => canEdit && toggleScreen(screen.id)}
                        className={cn(
                          'flex items-center gap-3 px-2 py-0.5 transition-colors',
                          canEdit
                            ? 'cursor-pointer hover:border-primary'
                            : 'pointer-events-none',
                          isScreenActive ? '' : 'opacity-60',
                        )}
                      >
                        <div className='w-5 shrink-0 text-center text-sm font-medium tabular-nums'>
                          {index + 1}
                        </div>
                        <VisualCanvas
                          encounterId={linkedVisual.encounterId ?? encounterId}
                          imageIndex={screen.image_index}
                          shapes={screen.shapes}
                          zoom={screen.zoom}
                          crop={screen.crop}
                          zoomable={false}
                          showText={false}
                          className='w-16 shrink-0 overflow-hidden rounded-md border border-border'
                        />
                        <div className='grow text-xs text-muted-foreground'>
                          {t(
                            'dashboard.team.plans.assignments.visual-screen-to-assign',
                            { count: countScreenEntitySlots(screen.shapes) },
                          )}
                        </div>
                        <Switch
                          checked={isScreenActive}
                          disabled={!canEdit}
                          tabIndex={-1}
                          className='pointer-events-none'
                        />
                      </Card>
                    )
                  })}
                </div>
              </div>
            ) : null}
            <div className='flex items-center gap-2'>
              {ability.visual_id !== undefined ? (
                <a
                  href={visualUrl(ability.visual_id)}
                  target='_blank'
                  rel='noreferrer'
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'flex-1 gap-2',
                  )}
                >
                  <ExternalLink className='h-4 w-4' />
                  {t('dashboard.team.plans.assignments.visual-open')}
                </a>
              ) : null}
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='gap-2'
                onClick={() => {
                  onUpdate((current) => {
                    current.visual_id = undefined
                  }, true)
                }}
                disabled={!canEdit}
              >
                <Trash2 className='h-4 w-4' />
                {t('dashboard.team.plans.assignments.visual-unlink')}
              </Button>
            </div>
          </div>
        ) : null}

        {showChooser ? (
          <div className='grid grid-cols-2 gap-2'>
            {options.map((option) => (
              <Card
                key={option.key}
                role='button'
                tabIndex={canEdit ? 0 : -1}
                onClick={() => canEdit && option.onSelect()}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 text-center transition-colors',
                  canEdit
                    ? 'cursor-pointer hover:border-primary'
                    : 'pointer-events-none opacity-50',
                )}
              >
                <span className='flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary'>
                  <option.icon className='h-4 w-4' />
                </span>
                <span className='text-sm font-medium'>{option.title}</span>
              </Card>
            ))}
          </div>
        ) : null}

        {showPicker ? (
          <div className='space-y-2'>
            <SubviewHeader
              title={t('dashboard.team.plans.assignments.visual-create-title')}
              onBack={() => setView('chooser')}
              actions={
                canEdit ? (
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    className='gap-1.5'
                    onClick={() => createMutation.mutate()}
                    disabled={createMutation.isPending}
                  >
                    <Plus className='h-4 w-4' />
                    {t('dashboard.team.plans.assignments.visual-create-new')}
                  </Button>
                ) : null
              }
            />
            {(encounterVisuals ?? []).length === 0 ? (
              <div className='text-sm text-muted-foreground'>
                {t('dashboard.team.plans.assignments.visual-link-empty')}
              </div>
            ) : (
              <div className='grid grid-cols-3 gap-2'>
                {(encounterVisuals ?? []).map((visual) => (
                  <button
                    key={visual.id}
                    type='button'
                    className='text-left'
                    onClick={() => {
                      onUpdate((current) => {
                        current.visual_id = visual.id
                        current.raid_plan_link = undefined
                      }, true)
                      setView('chooser')
                    }}
                    disabled={!canEdit}
                  >
                    <VisualPlanThumbnail visual={visual} />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {showRaidstrats ? (
          <div className='space-y-2'>
            <SubviewHeader
              title={t(
                'dashboard.team.plans.assignments.visual-raidstrats-title',
              )}
              onBack={visualPlansEnabled ? () => setView('chooser') : undefined}
            />
            <div className='flex items-center gap-2'>
              <Input
                value={ability.raid_plan_link ?? ''}
                onChange={(event) =>
                  onUpdate((current) => {
                    current.raid_plan_link = event.target.value || undefined
                  }, false)
                }
                onPaste={(event) => {
                  const pasted = event.clipboardData.getData('text')
                  const extractedUuid = extractUuid(pasted)
                  if (!extractedUuid) return
                  event.preventDefault()
                  onUpdate((current) => {
                    current.raid_plan_link = extractedUuid
                  }, false)
                }}
                onBlur={onCommit}
                placeholder={t(
                  'dashboard.team.plans.assignments.raid-plan-link-placeholder',
                )}
                className='h-8 text-xs'
                disabled={!canEdit}
                autoFocus={!hasLink}
              />
              {linkInfo.hasRaidPlanId ? (
                <a
                  href={linkInfo.url}
                  target='_blank'
                  rel='noreferrer'
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'icon' }),
                    'h-8 w-8 shrink-0',
                  )}
                >
                  <ExternalLink className='h-4 w-4' />
                </a>
              ) : (
                <span
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'icon' }),
                    'h-8 w-8 shrink-0 pointer-events-none opacity-50',
                  )}
                >
                  <ExternalLink className='h-4 w-4' />
                </span>
              )}
              <Button
                type='button'
                variant='outline'
                size='icon'
                className='h-8 w-8 shrink-0'
                onClick={() => {
                  onUpdate((current) => {
                    current.raid_plan_link = undefined
                  }, true)
                  setView('chooser')
                }}
                disabled={!canEdit || !hasLink}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

export default AbilityVisualPopover
